import React, { useState, useEffect, useContext } from 'react';
import SweetScrollContext from 'utils/SweetScrollContext';
import RichText from 'components/RichText';
import Button from 'components/Core/Button';
import WistiaVideo from 'components/Core/WistiaVideo';
import WebsiteImage from 'components/WebsiteImage';
import useBreakpointView from 'utils/useBreakpointView';
import useHash from 'hooks/useHash';
import richTextOptions from './richTextOptions';
import leftArrow from '../images/left-arrow.svg';
import rightArrow from '../images/right-arrow.svg';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import {
  InPageFeaturesV6Container,
  FeaturedContentSlider,
  FeaturedContentInnerContainer,
} from './styles';

const InPageFeaturesV6 = ({ initialSlide, inPageNavigationItems, backgroundColor, title }) => {
  const [step, setStep] = useState(initialSlide || false);
  const [isSlickMoving, setSlickMoving] = useState(false);
  const [hash, updateHash] = useHash();
  const { scrollToAnchor } = useContext(SweetScrollContext);

  const isMobile = useBreakpointView(['xs', 'sm']);
  let slider;
  const featureSliderSettings = {
    dots: true,
    dotsClass: 'slick-dots slick-thumb',
    autoplay: false,
    arrows: false,
    infinite: false,
    variableWidth: true,
    appendDots: (dots) => <ul onClick={() => updateHash('')}>{dots}</ul>,
    onSwipe: () => updateHash(''),
    beforeChange: () => {
      setTimeout(() => {
        setSlickMoving(false);
      }, 600);
    },
    afterChange: (index) => {
      if (!isSlickMoving) {
        handleSlickMove(index);
      }
    },
  };

  useEffect(() => {
    initialSlide && setStep(initialSlide);
  }, [initialSlide]);

  const handlePrevClick = () => {
    updateHash('');
    if (isSlickMoving) {
      return;
    }
    setSlickMoving(true);
    setStep((prevStep) => (prevStep >= 1 ? prevStep - 1 : prevStep));
  };

  const handleNextClick = () => {
    updateHash('');
    if (isSlickMoving) {
      return;
    }
    setSlickMoving(true);
    setStep((prevStep) =>
      prevStep <= inPageNavigationItems?.length - 2 ? prevStep + 1 : prevStep,
    );
  };

  const handleSlickMove = (index) => {
    setStep(index);
  };

  useEffect(() => {
    if (slider && step !== false) {
      slider.slickGoTo(step);
    }
  }, [step]);

  useEffect(() => {
    if (hash) {
      inPageNavigationItems.forEach((navItem, index) => {
        const sectionId = hash.split('-')?.[1];
        if (sectionId === navItem.contentful_id) {
          setStep(index);
          setTimeout(() => {
            scrollToAnchor(`#section-${sectionId}`, { updateURL: false });
          }, 900);
        }
      });
    }
  }, [hash]);

  const renderFeaturedItem = ({
    disclaimer,
    stat,
    featuredImage,
    featuredVideo,
    description,
    tags,
  }) => {
    const itemType =
      featuredImage?.image?.file?.contentType === 'video/mp4' ||
      featuredImage?.bynderDesktopImage?.[0]?.videoPreviewURLs?.[0]?.endsWith('mp4')
        ? 'mp4'
        : featuredVideo
        ? 'wistiaVideo'
        : 'image';

    const featuredContent =
      itemType === 'image' || itemType === 'mp4' ? (
        <FeaturedContentInnerContainer>
          <WebsiteImage {...featuredImage} />
        </FeaturedContentInnerContainer>
      ) : (
        <WistiaVideo dataPosition="In-Page Features" url={featuredVideo?.sharableLink} />
      );

    return (
      <div className="in-page-commercial--featured-item-wrapper">
        <div className="in-page-commercial--featured-item-image">{featuredContent}</div>
        <div className="in-page-commercial--featured-item-copy">
          <RichText content={description} options={richTextOptions} />
          {stat && (
            <div className="in-page-features-stat">
              <RichText content={stat} options={richTextOptions} />
            </div>
          )}
          {disclaimer && (
            <div className="in-page-features-disclaimer">
              <RichText content={disclaimer} options={richTextOptions} />
            </div>
          )}
          {tags?.map((tag) => {
            const title = tag.split(':')[0];
            const link = tag.split(':')[1]?.trimStart();

            return (
              <Button type="transparent - black border" to={link} className="tag font-p">
                {title}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFeaturedItemList = () =>
    inPageNavigationItems.map((feature, index) => (
      <div
        className="in-page-commercial--featured-item"
        id={`section-${feature.contentful_id}`}
        key={index}
      >
        {renderFeaturedItem({ ...feature })}
      </div>
    ));

  return (
    <InPageFeaturesV6Container
      className="in-page-commercial--container"
      backgroundColor={backgroundColor}
    >
      {title && <h2 className="in-page-commercial--title">{title}</h2>}
      <div className="in-page-commercial--wrapper">
        <div className="in-page-commercial--featured-content">
          {inPageNavigationItems &&
            (!isMobile ? (
              <div className="in-page-commercial--featured-content-wrapper min-md">
                <FeaturedContentSlider ref={(c) => (slider = c)} {...featureSliderSettings}>
                  {renderFeaturedItemList()}
                </FeaturedContentSlider>
                {step > 0 && (
                  <div
                    className="in-page-commercial--featured-slider-arrow arrow-left"
                    onClick={handlePrevClick}
                  >
                    <img src={leftArrow} alt="Left Arrow" />
                    <span>{inPageNavigationItems[step - 1]?.title || ''}</span>
                  </div>
                )}
                {step < inPageNavigationItems.length - 1 && (
                  <div
                    className="in-page-commercial--featured-slider-arrow arrow-right"
                    onClick={handleNextClick}
                  >
                    <img src={rightArrow} alt="Right Arrow" />
                    <span>{inPageNavigationItems[step + 1]?.title || ''}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="in-page-commercial--featured-content-wrapper max-md">
                {renderFeaturedItemList()}
              </div>
            ))}
        </div>
      </div>
    </InPageFeaturesV6Container>
  );
};

export default InPageFeaturesV6;
